// Copyright © Orpyx® Medical Technologies Inc. 2026. All rights reserved.
// **ORPYX TRADE SECRET**
// **WARNING**: The contents of this repository, including source code and assets, are confidential. This source code and/or derived works are a trade secret of Orpyx Medical Technologies Inc. Unauthorized disclosure is strictly prohibited and may result in serious legal consequences.

import * as React from "react";
import {memo} from "react";
import {connect} from "react-redux";
import { usePatientInformationPageStyles } from "../patient-information-page/patient-information-page.style";
import { InsightImageStatus, PatientInformationDailyInsightsMeta, PatientInformationDailyInsightsOwnProps } from "./patient-information-daily-insights.interface";
import {PatientInformationDailyInsightsSelectors} from "./store/patient-information-daily-insights.selectors";
import {PatientInformationPageSelectors} from "../../store/patient-information-page.selectors";
import { Box, CircularProgress, Grid, IconButton } from "@material-ui/core";
import { IOrpyxApplicationStore } from "modules/store/application.reducers";
import { PatientInformationTitle } from "../../../patient-information-title/patient-information-title";
import { NAVIGATE_TO_DAILY_INSIGHTS_LATEST, NAVIGATE_TO_DAILY_INSIGHTS_NEXT, NAVIGATE_TO_DAILY_INSIGHTS_PREVIOUS, RELOAD_CURRENT_INSIGHT_IMAGE } from "./store/patient-information-daily-insights.actions";
import _ from "underscore";
import { useCircularProgressStyles } from "modules/shared/components/circular-progress/circular-progress-styles";
import { Refresh } from "@material-ui/icons";


export const ERROR_TEXT = "Unable to retrieve Daily Insights. Try refreshing the page. If the problem persists, please provide the patient ID and date being viewed in a bug triage report"
export const ERROR_TEXT_SINGLE_IMAGE = "Unable to retrieve Daily Insights for this date. Try clicking the refresh button above. If the problem persists, please provide the patient ID and date being viewed in a bug triage report"

const PatientInformationDailyInsightsContent = memo((props: PatientInformationDailyInsightsMeta) => {
    const classes = usePatientInformationPageStyles();
    const commonClasses = useCircularProgressStyles();

    if (props.dailyInsightState.error && !props.dailyInsightState.initialized) {
        return (
            <Grid container spacing={3} id="patient-information-insights-grid">
                <Grid item xs={12}>
                    <div className={classes.content}>
                        <p>Error: {typeof props.dailyInsightState.error === 'string' ? props.dailyInsightState.error : ERROR_TEXT}</p>
                    </div>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={3} id="patient-information-insights-grid">
            <Grid item xs={12}>
                <div className={classes.content}>
                    {props.dailyInsightState.currentImage ? (
                        <div>

                            <div className={classes.horizontalLayout}>
                                <Grid item>
                                <div>
                                    {props.dailyInsightState.currentImage.date && (
                                        <span className={classes.bold}>
                                            Daily Insights for {props.dailyInsightState.currentImage.date} 
                                        </span>
                                    )}
                                </div>
                                </Grid>
                                <Grid item xs />
                                
                                <Grid item>
                                    <IconButton
                                    className={classes.dailyInsightNavControl}
                                    onClick={props.reloadInsightFigure}
                                    title="Refresh current figure" >
                                        {
                                            props.dailyInsightState.currentImage.status == InsightImageStatus.ERROR ? 
                                        <>
                                        { 
                                            props.dailyInsightState.loading && props.dailyInsightState.refreshing ? 
                                            <>
                                            <Box className={commonClasses.flexWrapper}><CircularProgress className={commonClasses.buttonProgress}/>
                                            </Box>
                                            <span> <Refresh/> </span>
                                            </>
                                             :
                                            <span> <Refresh/> </span>
                                        }
                                        </> : 
                                        <></>
                                        }
                                    </IconButton>
                                    <IconButton
                                        className={`${classes.dailyInsightNavControl} ${commonClasses.wrapper}`}
                                        onClick={ props.navigateToPrevious }
                                        disabled={!props.dailyInsightState.hasPrevious}
                                        title="Go to previous insight" >
                                        { 
                                            props.dailyInsightState.loading && !props.dailyInsightState.refreshing ? 
                                            <>
                                            <Box className={commonClasses.flexWrapper}><CircularProgress className={commonClasses.buttonProgress}/>
                                            </Box>
                                            <span>◀</span>
                                            </>
                                             :
                                            <span>◀</span>
                                        }
                                    </IconButton>



                                    <IconButton
                                        className={classes.dailyInsightNavControl}
                                        onClick={props.navigateToNext}
                                        disabled={!props.dailyInsightState.hasNext}
                                        title="Go to next insight" >
                                        <span>▶</span>
                                    </IconButton>
                                    <IconButton
                                        className={classes.dailyInsightNavControl}
                                        onClick={props.navigateToLatest}
                                        disabled={props.dailyInsightState.currentImageIndex===0}
                                        title="Go to latest insight" >
                                        <span>▶▶</span>
                                    </IconButton>
                                </Grid>
                            </div>

                            <Box className={commonClasses.wrapper}>

                            {props.dailyInsightState.currentImage.status === InsightImageStatus.READY ?
                                 <img src={props.dailyInsightState.currentImage.blobUrl!}
                                alt={`Daily Insights for ${props.dailyInsightState.currentImage.date}`}
                                className={`${classes.dailyInsightImage} ${classes.centered}`} />
                                : 
                                <></>
                            }

                            {props.dailyInsightState.currentImage.status === InsightImageStatus.LOADING ?  <CircularProgress/>: <></> }

                            {props.dailyInsightState.currentImage.status === InsightImageStatus.ERROR ?
                            <Box>
                                {ERROR_TEXT_SINGLE_IMAGE}
                            </Box> : <></> }
                            </Box>
                            </div>
                    ) : (
                        <div>
                            <p>No daily insights available for this patient.</p>
                        </div>
                    )}
                </div>
            </Grid>
        </Grid>
    );
});

const PatientInformationDailyInsightsLoading = memo((props: PatientInformationDailyInsightsMeta) => {
    const classes = usePatientInformationPageStyles();
    return (
        <Grid container spacing={2} className={classes.horizontalLayout}>
            <Grid item xs={12}>
                <div className={classes.content}>
                    {props.dailyInsightState.error ? (
                        <p>Error: {typeof props.dailyInsightState.error === 'string' ? props.dailyInsightState.error : ERROR_TEXT}</p>
                    ) : (
                        <div className={classes.centered}> <CircularProgress/> Loading insight data </div>
                    )}
                </div>
            </Grid>
        </Grid>
    );
});

const PatientInformationDailyInsightsTemplate = (props: PatientInformationDailyInsightsMeta) => {
    if (!props.dailyInsightState.initialized) {
        return (
            <>
                <PatientInformationTitle text="Daily Insights" />
                <PatientInformationDailyInsightsLoading {...props}/>
            </>
        );
    }

    return (
        <>
            <PatientInformationTitle text="Daily Insights" />
            <PatientInformationDailyInsightsContent {...props}/>
        </>
    );
};

const mapStateToProps = (state: IOrpyxApplicationStore): PatientInformationDailyInsightsOwnProps => {
    const patient = PatientInformationPageSelectors.patient(state);
    const currentImageIndex = PatientInformationDailyInsightsSelectors.currentImageIndex(state);
    return {
        patientId: patient ? patient.patientId! : undefined,
        dailyInsightState: {
            error: PatientInformationDailyInsightsSelectors.error(state),
            initialized: PatientInformationDailyInsightsSelectors.initialized(state),
            loading: PatientInformationDailyInsightsSelectors.loading(state),
            refreshing: PatientInformationDailyInsightsSelectors.refreshing(state),
            hasPrevious: PatientInformationDailyInsightsSelectors.hasPrevious(state),
            hasNext: PatientInformationDailyInsightsSelectors.hasNext(state),
            currentImageIndex: currentImageIndex,
            currentImage: PatientInformationDailyInsightsSelectors.currentImage(state)!,
        }
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    reloadInsightFigure: () => dispatch(RELOAD_CURRENT_INSIGHT_IMAGE()),
    navigateToNext: () => dispatch(NAVIGATE_TO_DAILY_INSIGHTS_NEXT()),
    navigateToPrevious: () => dispatch(NAVIGATE_TO_DAILY_INSIGHTS_PREVIOUS()),
    navigateToLatest: () => dispatch(NAVIGATE_TO_DAILY_INSIGHTS_LATEST())
});

export const PatientInformationDailyInsights = memo(connect(mapStateToProps, mapDispatchToProps)(PatientInformationDailyInsightsTemplate));
